import { useMemo } from 'react';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import {
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableFeatures,
  type TableCellProps,
} from '@fluentui/react-components';
import AvatarGroup from '@modules/users/components/AvatarGroup';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { SmartTableCell } from '@shared/components/tables/SmartTableCell';
import ArchiveTableAction from '@shared/components/tables/ArchiveTableAction';
import { DateLabel } from '@shared/components/dates/DateLabel';
import { usePersistedColumnSizing } from '@shared/hooks';
import ProjectStatusBadge from './ProjectStatusBadge';
import { getProjectPath } from '../paths';
import { PROJECTS_TABLE_ID } from '../constants';
import type { TProjectListItem } from '../types';

type TColumnId = 'name' | 'status' | 'start' | 'due' | 'leads';

// autoFitColumns stays false - true would re-run width calculation on every render and overwrite user drag-resize.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    name: { defaultWidth: 320, minWidth: 160 },
    status: { defaultWidth: 110, minWidth: 90 },
    start: { defaultWidth: 110, minWidth: 90 },
    due: { defaultWidth: 110, minWidth: 90 },
    leads: { defaultWidth: 140, minWidth: 100 },
  },
};

const columnIds: TColumnId[] = ['name', 'status', 'start', 'due', 'leads'];

const ProjectNameCell = ({
  project,
  cellProps,
  onArchive,
}: {
  project: TProjectListItem;
  cellProps: TableCellProps;
  onArchive: (project: TProjectListItem) => void;
}) => {
  const { canEdit } = useCanEdit(project.accessRight, { authorGroup: false });
  return (
    <SmartTableCell
      cellProps={cellProps}
      variant={{ kind: 'link', href: getProjectPath(project._id), label: project.name }}
      openMode="sameTab"
      tooltip={{ threshold: 70 }}
      after={canEdit && <ArchiveTableAction label="Archive" onArchive={() => onArchive(project)} />}
    />
  );
};

const ProjectsTable = ({
  projects,
  onArchive,
}: {
  projects: TProjectListItem[];
  onArchive: (project: TProjectListItem) => void;
}) => {
  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<TProjectListItem>({ columnId })), []);
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items: projects, getRowId: (project) => project._id },
    [usePersistedColumnSizing(PROJECTS_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();

  const tableClasses = useItemTableStyles();
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('name')}>
              <Body1Strong>Name</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('status')}>
              <Body1Strong>Status</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('start')}>
              <Body1Strong>Start date</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('due')}>
              <Body1Strong>Due date</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('leads')}>
              <Body1Strong>{TERMINOLOGY.project.upper} leads</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item: project }) => (
            <TableRow key={project._id}>
              <ProjectNameCell
                project={project}
                cellProps={columnSizing_unstable.getTableCellProps('name')}
                onArchive={onArchive}
              />
              <TableCell {...columnSizing_unstable.getTableCellProps('status')}>
                <ProjectStatusBadge status={project.status} />
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('start')}>
                {project.startDate ? (
                  <DateLabel short timeZone="UTC">
                    {project.startDate}
                  </DateLabel>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('due')}>
                {project.dueDate ? (
                  <DateLabel short timeZone="UTC">
                    {project.dueDate}
                  </DateLabel>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('leads')}>
                <AvatarGroup ids={project.leads} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default ProjectsTable;
